import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Loader2, TrendingUp, Clock } from 'lucide-react';
import api from '../lib/api';

const RECENT_KEY = 'va_recent_searches';
const DEFAULT_TRENDING = ['Maskking', 'Ay Papa', 'Fasta', 'e-juice', 'Pods', 'Vape desechable', 'Kits', 'Resistencias'];

// Read popular searches from window.__vaSettings (populated by SiteHead, same as brand settings)
function getPopularSearches() {
  try {
    const cfg = window.__vaSettings || {};
    if (Array.isArray(cfg.popular_searches) && cfg.popular_searches.length) return cfg.popular_searches;
  } catch {}
  return DEFAULT_TRENDING;
}

const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [trending, setTrending] = useState(getPopularSearches);
  const timer = useRef(null);
  const inputRef = useRef(null);

  // Re-read when overlay opens (settings may have loaded after mount)
  useEffect(() => {
    if (open) setTrending(getPopularSearches());
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      try {
        const r = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        setRecent(r.slice(0, 6));
      } catch { setRecent([]); }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get(`/products?q=${encodeURIComponent(q.trim())}&limit=8`)
        .then((r) => setResults(r.data.items))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const saveRecent = (term) => {
    if (!term) return;
    try {
      const existing = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const filtered = existing.filter((s) => s.toLowerCase() !== term.toLowerCase());
      const next = [term, ...filtered].slice(0, 10);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  };

  const submit = (term) => {
    const value = (term || q).trim();
    if (!value) return;
    saveRecent(value);
    onClose(`/catalogo?q=${encodeURIComponent(value)}`);
  };

  const onKey = (e) => {
    if (e.key === 'Enter') submit();
    if (e.key === 'Escape') onClose();
  };

  const clearRecent = () => {
    localStorage.removeItem(RECENT_KEY);
    setRecent([]);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex flex-col animate-fade-in">
      <div className="flex items-center gap-2 md:gap-3 px-4 md:px-6 py-3 border-b border-white/10 bg-black/80">
        <div className="relative flex-1 max-w-3xl mx-auto flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-5 h-5 text-orange-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              type="text"
              placeholder="Encuentra tu vape perfecto 🔥"
              className="w-full h-12 md:h-14 rounded-full bg-neutral-900 border border-white/15 text-white pl-12 pr-12 outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 placeholder:text-white/40 text-base md:text-lg"
              style={{ color: '#fff', caretColor: '#fff', colorScheme: 'dark' }}
              aria-label="Búsqueda"
            />
            {loading && (
              <Loader2 className="w-5 h-5 text-white/60 absolute right-14 top-1/2 -translate-y-1/2 animate-spin" />
            )}
            {q && (
              <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Limpiar">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button onClick={() => onClose()} className="h-12 px-4 md:px-5 rounded-full bg-white/5 hover:bg-white/10 text-white/80 hover:text-white font-semibold text-sm shrink-0">
            Cancelar
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
          {!q && (
            <>
              {recent.length > 0 && (
                <section className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Recientes</h3>
                    <button onClick={clearRecent} className="text-xs text-white/50 hover:text-white">Limpiar</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button key={r} onClick={() => submit(r)} className="h-9 px-4 rounded-full bg-white/5 border border-white/10 text-white/85 hover:border-orange-500/50 hover:bg-orange-500/10 hover:text-white text-sm">
                        {r}
                      </button>
                    ))}
                  </div>
                </section>
              )}
              <section>
                <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider flex items-center gap-2 mb-3"><TrendingUp className="w-3.5 h-3.5" /> Búsquedas populares</h3>
                <div className="flex flex-wrap gap-2">
                  {trending.map((t) => (
                    <button key={t} onClick={() => submit(t)} className="h-9 px-4 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500 hover:text-black text-sm font-semibold">
                      {t}
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}

          {q && results.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">Productos ({results.length})</h3>
              {results.map((p) => (
                <Link
                  key={p.product_id}
                  to={`/producto/${p.handle}`}
                  onClick={() => { saveRecent(q.trim()); onClose(); }}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors"
                >
                  <div className="w-14 h-14 rounded-lg bg-neutral-900 overflow-hidden shrink-0">
                    {p.main_image && <img src={p.main_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-orange-400 uppercase tracking-wider">{p.vendor}</p>
                    <p className="text-white font-semibold text-sm truncate">{p.title}</p>
                  </div>
                  <p className="font-black text-white shrink-0">${p.price?.toLocaleString('es-MX')}</p>
                </Link>
              ))}
              <button onClick={() => submit()} className="w-full mt-2 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm">
                Ver todos los resultados para "{q}"
              </button>
            </section>
          )}

          {q && q.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="text-center py-16">
              <Search className="w-10 h-10 mx-auto text-white/30" />
              <p className="mt-3 text-white/70 font-semibold">Sin resultados para "{q}"</p>
              <p className="text-white/50 text-sm">Prueba con otro término o revisa las categorías.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
